/**
 * 财务中心 - 列表页滚动加载公共逻辑（交易明细/实时余额/子账号管理三页共用）
 *
 * 适配"银行单批返回 + 游标续传"的列表接口（统一响应契约 records/count/hasMore/cursor/truncated，
 * 游标对象由后端透出、前端原样回传，字段随接口不同：交易明细为 ctnDum/ctnDat/ctnSeq 三件套，
 * 余额与子单元列表为 rsv50z）。页面用法：
 * 1. el-table 设固定高度（滚动发生在表格 bodyWrapper 上）并加 ref，mounted 后调用
 *    bindTableScroll('表格 ref 名') 绑定滚动监听，组件销毁时自动解绑防内存泄漏；
 * 2. 首次查询与查询条件变化重置时调用 loadFirstBatch()（清空缓冲区与游标后从首屏重拉）；
 * 3. 滚动到距表格底部 50px 内且还有下一批时自动携带当前游标与查询条件追加下一批，
 *    加载中设闸门防快速连滚重复请求；横向滚动不触发追加（纵向位置未变化即视为横向滚动）；
 *    追加失败时缓冲区与游标保留，可继续滚动重试或重新查询；
 *    内容未撑满表格容器时不依赖滚动事件，自动补拉下一批直到撑满出现纵向滚动条或取完为止；
 * 4. 页面须实现两个方法：buildQueryParams()（返回当前查询条件参数，不含游标）、
 *    fetchBatch(params)（调用列表接口，成功返回响应 data 契约对象，失败返回 null 并自行提示）；
 *    可选实现 afterBatchLoaded(append)（本批落库后的页面侧处理，如固化账号）；
 * 5. 首屏加载复用页面自身的 listLoading 状态（须在页面 data 中声明），追加加载用 scrollLoading。
 */
export default {
  data() {
    return {
      // 已加载记录缓冲区（首屏 + 历次追加的累积）
      rows: [],
      // 下一批游标（后端透传对象，null=没有下一批）
      cursor: null,
      // 是否还有下一批
      hasMore: false,
      // 银行续传异常截断标记（true 时提示结果可能不完整）
      truncated: false,
      // 追加加载中（滚动触发闸门，防连滚重复请求）
      scrollLoading: false,
      // 是否已成功查询过（控制提示行展示）
      queried: false,
      // 已绑定滚动监听的表格 ref 名
      scrollTableRef: '',
      // 请求代际序号（首屏加载时递增，用于丢弃条件重置前发出的过期响应，防止旧数据旧游标清入新结果）
      requestSeq: 0,
      // 上一次滚动时的纵向位置（用于区分横向滚动与纵向滚动，横向滚动不触发追加）
      lastScrollTop: 0
    };
  },
  beforeDestroy() {
    this.unbindTableScroll();
  },
  methods: {
    // 绑定表格滚动监听（表格须设固定高度，滚动容器为 el-table 的 bodyWrapper）
    bindTableScroll(refName) {
      this.scrollTableRef = refName;
      this.$nextTick(() => {
        const wrapper = this.getTableBodyWrapper();
        if (wrapper) {
          wrapper.addEventListener('scroll', this.handleTableScroll);
        }
      });
    },
    // 解绑滚动监听（组件销毁时调用，防监听器泄漏）
    unbindTableScroll() {
      if (!this.scrollTableRef) {
        return;
      }
      const wrapper = this.getTableBodyWrapper();
      if (wrapper) {
        wrapper.removeEventListener('scroll', this.handleTableScroll);
      }
      this.scrollTableRef = '';
    },
    // 取表格滚动容器（bodyWrapper），表格未渲染或 ref 未挂载时返回 null
    getTableBodyWrapper() {
      const table = this.$refs[this.scrollTableRef];
      return (table && table.bodyWrapper) || null;
    },
    // 滚动到距底部 50px 内时触发追加（纵向位置未变化=横向滚动事件，直接忽略）
    handleTableScroll() {
      const wrapper = this.getTableBodyWrapper();
      if (!wrapper) {
        return;
      }
      if (wrapper.scrollTop === this.lastScrollTop) {
        return;
      }
      this.lastScrollTop = wrapper.scrollTop;
      if (wrapper.scrollHeight - wrapper.scrollTop - wrapper.clientHeight > 50) {
        return;
      }
      this.loadNextBatch();
    },
    // 追加下一批（有闸门：已查过、还有下一批且有游标、非加载中才发请求），追加后检查内容是否撑满容器，未撑满继续自动补批
    async loadNextBatch() {
      if (!this.queried || !this.hasMore || !this.cursor || this.scrollLoading) {
        return;
      }
      await this.loadBatch(true);
      await this.autoFillVisible();
    },
    // 内容未撑满表格容器（无纵向滚动条）时自动补拉下一批，直到撑满或取完为止；
    // 自动补批最多连续 50 批防异常数据下死循环，补批失败即停（缓冲区与游标保留，可滚动重试）
    async autoFillVisible() {
      const wrapper = this.getTableBodyWrapper();
      if (!wrapper) {
        return;
      }
      let guard = 0;
      while (this.queried && this.hasMore && this.cursor && !this.scrollLoading && guard < 50) {
        await new Promise(resolve => setTimeout(resolve, 50));
        if (wrapper.scrollHeight > wrapper.clientHeight) {
          return;
        }
        guard++;
        const ok = await this.loadBatch(true);
        if (!ok) {
          return;
        }
      }
    },
    // 清空缓冲区与游标（查询条件变化时先调用，随后 loadFirstBatch 从首屏重拉，旧游标不残留）
    resetScrollState() {
      this.rows = [];
      this.cursor = null;
      this.hasMore = false;
      this.truncated = false;
      this.lastScrollTop = 0;
    },
    // 首屏加载：代际递增使飞行中的旧请求响应全部失效，重置后拉第一批（走页面 listLoading 全程 loading），
    // 落地后检查内容是否撑满容器，未撑满自动补批
    async loadFirstBatch() {
      this.requestSeq++;
      this.resetScrollState();
      await this.loadBatch(false);
      await this.autoFillVisible();
    },
    // 拉取一批记录（append=true 带游标追加，false 首屏覆盖）；追加有加载中闸门防连滚重复请求，
    // 首屏不受闸门拦截（允许打断进行中的追加）；接口失败或异常时缓冲区与游标保留不动，
    // 响应落地前校验代际，条件已重置的过期响应直接丢弃；返回是否成功落地一批
    async loadBatch(append) {
      if (append && this.scrollLoading) {
        return false;
      }
      const seq = this.requestSeq;
      this.scrollLoading = true;
      if (!append) {
        this.listLoading = true;
      }
      try {
        const params = this.buildQueryParams();
        if (append && this.cursor) {
          Object.keys(this.cursor).forEach(key => {
            params[key] = this.cursor[key];
          });
        }
        const data = await this.fetchBatch(params);
        if (!data || seq !== this.requestSeq) {
          return false;
        }
        const batch = data.records || [];
        this.rows = append ? this.rows.concat(batch) : batch;
        this.cursor = data.cursor || null;
        this.hasMore = !!data.hasMore;
        this.truncated = !!data.truncated;
        this.queried = true;
        if (this.afterBatchLoaded) {
          this.afterBatchLoaded(append);
        }
        return true;
      } catch (e) {
        // 接口异常（如 HTTP 400/500）按失败处理：首屏保持空列表，追加保留已加载数据与游标，提示已由响应拦截器统一弹出
        return false;
      } finally {
        this.scrollLoading = false;
        if (!append) {
          this.listLoading = false;
        }
      }
    }
  }
};



// WEBPACK FOOTER //
// ./src/views/finance/financeScrollList.js